// progress.ts — the state behind the progress modal: a list of steps, which one is running, a real percentage when
// the engine reports one, and what is happening right now. Pure, so the step logic tests without a browser.
export type StepState = "done" | "running" | "todo" | "failed";
export interface Progress {
  title: string;
  steps: string[];
  current: number;                 // index of the running step (steps.length = all done)
  detail: string | null;           // what is happening right now, in words
  pct: number | null;              // a REAL percentage from the engine, or null (the bar then shows activity, not a guess)
  state: "running" | "done" | "error";
  error: string | null;
  startedAt: number;
  links: { label: string; href: string }[];
}

export const startProgress = (title: string, steps: string[], now = Date.now()): Progress =>
  ({ title, steps, current: 0, detail: null, pct: null, state: "running", error: null, startedAt: now, links: [] });

/** Move to step `i` (earlier steps become done), optionally with what's happening and a real percentage. */
export const atStep = (p: Progress, i: number, detail: string | null = null, pct: number | null = null): Progress =>
  ({ ...p, current: Math.max(0, Math.min(i, p.steps.length)), detail, pct: pct == null ? null : Math.max(0, Math.min(100, pct)) });

export const finish = (p: Progress, detail: string | null = null, links: Progress["links"] = []): Progress =>
  ({ ...p, current: p.steps.length, state: "done", detail, pct: 100, links });

export const fail = (p: Progress, error: string): Progress => ({ ...p, state: "error", error });

export function stepStates(p: Progress): StepState[] {
  return p.steps.map((_, i) => (i < p.current ? "done" : i === p.current ? (p.state === "error" ? "failed" : p.state === "done" ? "done" : "running") : "todo"));
}

/** "12 s", "1 min 05 s". */
export function elapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")} s`;
}

/** A run's engine status (RunStatus) → the words and the percentage the modal shows. */
export function describeRun(s: { status?: string; progress?: { phase?: string; pct?: number; done?: number; total?: number } | null;
  gate?: { gateName: string } | null; failure?: { reason: string | null } | null } | null): { detail: string; pct: number | null; settled: "review" | "done" | "failed" | null } {
  if (!s) return { detail: "waiting for the engine…", pct: null, settled: null };
  if (s.status === "failed" || s.status === "cancelled") return { detail: s.failure?.reason ?? `the run ${s.status}`, pct: null, settled: "failed" };
  if (s.status === "succeeded") return { detail: "finished", pct: 100, settled: "done" };
  if (s.gate) return { detail: `ready for your review (${s.gate.gateName})`, pct: s.progress?.pct ?? null, settled: "review" };
  const p = s.progress;
  const count = p?.done != null && p?.total ? ` · ${p.done} of ${p.total}` : "";
  return { detail: `${p?.phase ?? s.status ?? "working"}${count}`, pct: p?.pct ?? null, settled: null };
}
